import React, { useState, useCallback } from 'react';
import { tuning, resetTuning, saveTuning, DEFAULT_TUNING } from '../../data/tuning';
import { CARD_TEMPLATES } from '../../data/cards';
import { theme } from '../../styles/theme';
import { ZoneType } from '../../types/map';

interface TuningPanelProps {
  onTuningChange: () => void; // called after any value changes so parent can re-render
  isOpen: boolean;
  onClose: () => void;
}

type Section = 'player' | 'signal' | 'zones' | 'agent' | 'cards';

const ZONE_NAMES: ZoneType[] = ['commercial', 'transit', 'mixed-use', 'civic', 'park', 'residential', 'industrial'];

const CARD_ORDER = [
  'move', 'shortcut',
  'quickStep', 'sprint', 'fade',
  'sweep', 'counterSurveillance', 'informant', 'spotter',
  'hideInside', 'decoy', 'hotwire',
  'improvisedStrike', 'instinct',
];

export function TuningPanel({ onTuningChange, isOpen, onClose }: TuningPanelProps) {
  const [section, setSection] = useState<Section>('cards');
  const [, forceRender] = useState(0);

  const update = useCallback(() => {
    forceRender(n => n + 1);
    saveTuning();
    onTuningChange();
  }, [onTuningChange]);

  const handleReset = useCallback(() => {
    resetTuning();
    update();
  }, [update]);

  if (!isOpen) return null;

  return (
    <div style={s.panel}>
      {/* Header */}
      <div style={s.header}>
        <span style={s.headerTitle}>TUNING</span>
        <div style={s.headerActions}>
          <button style={s.resetButton} onClick={handleReset}>RESET</button>
          <button style={s.closeButton} onClick={onClose}>✕</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {([
          ['player', 'PLAYER'],
          ['signal', 'SIGNAL'],
          ['zones', 'ZONES'],
          ['agent', 'AGENT'],
          ['cards', 'CARDS'],
        ] as [Section, string][]).map(([key, label]) => (
          <button
            key={key}
            style={{ ...s.tab, ...(section === key ? s.tabActive : {}) }}
            onClick={() => setSection(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={s.content}>
        {section === 'player' && (
          <PlayerSection update={update} />
        )}
        {section === 'signal' && (
          <SignalSection update={update} />
        )}
        {section === 'zones' && (
          <ZonesSection update={update} />
        )}
        {section === 'agent' && (
          <AgentSection update={update} />
        )}
        {section === 'cards' && (
          <CardsSection update={update} />
        )}
      </div>
    </div>
  );
}

// --- Shared input component ---

function NumInput({ label, value, onChange, min, max, step, unit }: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}) {
  return (
    <div style={s.row}>
      <label style={s.label}>{label}</label>
      <div style={s.inputGroup}>
        <input
          type="range"
          min={min ?? 0}
          max={max ?? 100}
          step={step ?? 1}
          value={value}
          onChange={e => onChange(parseFloat(e.target.value))}
          style={s.slider}
        />
        <input
          type="number"
          min={min ?? 0}
          max={max ?? 9999}
          step={step ?? 1}
          value={value}
          onChange={e => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v)) onChange(v);
          }}
          style={s.numInput}
        />
        {unit && <span style={s.unit}>{unit}</span>}
      </div>
    </div>
  );
}

// --- Sections ---

function PlayerSection({ update }: { update: () => void }) {
  return (
    <>
      <div style={s.sectionLabel}>GENERAL</div>
      <NumInput label="Max AP" value={tuning.playerMaxAp} onChange={v => { tuning.playerMaxAp = v; update(); }} min={1} max={10} />
      <NumInput label="Hand size" value={tuning.handSize} onChange={v => { tuning.handSize = v; update(); }} min={1} max={10} />

      <div style={s.sectionLabel}>VISION CONE</div>
      <NumInput label="Angle" value={tuning.visionConeAngle} onChange={v => { tuning.visionConeAngle = v; update(); }} min={10} max={360} step={5} unit="°" />
      <NumInput label="Range" value={tuning.visionConeRange} onChange={v => { tuning.visionConeRange = v; update(); }} min={10} max={500} step={10} unit="m" />

      <div style={s.sectionLabel}>HIDE INSIDE</div>
      <NumInput label="Building range" value={tuning.hideRange} onChange={v => { tuning.hideRange = v; update(); }} min={10} max={200} step={5} unit="m" />
      <NumInput label="Hide duration" value={tuning.hideDuration} onChange={v => { tuning.hideDuration = v; update(); }} min={1} max={5} unit="turns" />

      <div style={s.sectionLabel}>INSTINCT</div>
      <NumInput label="Duration" value={tuning.instinctDuration} onChange={v => { tuning.instinctDuration = v; update(); }} min={1} max={5} unit="turns" />

      <div style={s.sectionLabel}>DECOY</div>
      <NumInput label="Signal strength" value={tuning.decoyStrength} onChange={v => { tuning.decoyStrength = v; update(); }} min={0.05} max={1} step={0.05} />
      <NumInput label="Signal radius" value={tuning.decoyRadius} onChange={v => { tuning.decoyRadius = v; update(); }} min={100} max={3000} step={50} unit="m" />
    </>
  );
}

function SignalSection({ update }: { update: () => void }) {
  return (
    <>
      <div style={s.sectionLabel}>GENERATION</div>
      <NumInput label="Idle dampening" value={tuning.idleDampening} onChange={v => { tuning.idleDampening = v; update(); }} min={0} max={1} step={0.05} />
      <NumInput label="Distance norm" value={tuning.distanceNormalization} onChange={v => { tuning.distanceNormalization = v; update(); }} min={100} max={1000} step={25} unit="m" />
      <NumInput label="Min threshold" value={tuning.minSignalThreshold} onChange={v => { tuning.minSignalThreshold = v; update(); }} min={0} max={0.5} step={0.01} />
    </>
  );
}

function ZonesSection({ update }: { update: () => void }) {
  return (
    <>
      <div style={s.sectionLabel}>ZONE SIGNAL MULTIPLIERS</div>
      {ZONE_NAMES.map(zone => (
        <NumInput
          key={zone}
          label={zone}
          value={tuning.zoneMultipliers[zone]}
          onChange={v => { tuning.zoneMultipliers[zone] = v; update(); }}
          min={0}
          max={3}
          step={0.1}
        />
      ))}
    </>
  );
}

function AgentSection({ update }: { update: () => void }) {
  return (
    <>
      <div style={s.sectionLabel}>PATROL AI</div>
      <NumInput label="Move range" value={tuning.agentMoveRange} onChange={v => { tuning.agentMoveRange = v; update(); }} min={50} max={1000} step={25} unit="m" />
      <NumInput label="Moves per turn" value={tuning.agentMovesPerTurn} onChange={v => { tuning.agentMovesPerTurn = v; update(); }} min={1} max={5} />
    </>
  );
}

function CardsSection({ update }: { update: () => void }) {
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  return (
    <>
      {CARD_ORDER.map(key => {
        const template = CARD_TEMPLATES[key];
        if (!template) return null;
        const ct = tuning.cards[key];
        if (!ct) return null;
        const isExpanded = expandedCard === key;
        const isDefault = JSON.stringify(ct) === JSON.stringify(DEFAULT_TUNING.cards[key]);

        return (
          <div key={key} style={s.cardBlock}>
            <button
              style={{ ...s.cardHeader, ...(isExpanded ? s.cardHeaderActive : {}) }}
              onClick={() => setExpandedCard(isExpanded ? null : key)}
            >
              <span style={s.cardCategory}>{template.category.slice(0, 3).toUpperCase()}</span>
              <span style={s.cardName}>{template.name}</span>
              {!isDefault && <span style={s.cardModified}>●</span>}
              <span style={s.cardChevron}>{isExpanded ? '▾' : '▸'}</span>
            </button>
            {isExpanded && (
              <div style={s.cardBody}>
                <NumInput label="AP cost" value={ct.apCost} onChange={v => { ct.apCost = v; update(); }} min={0} max={6} />
                {ct.movementRange !== undefined && (
                  <NumInput label="Move range" value={ct.movementRange} onChange={v => { ct.movementRange = v; update(); }} min={50} max={1500} step={25} unit="m" />
                )}
                {ct.signalModifier !== undefined && (
                  <NumInput label="Signal modifier" value={ct.signalModifier} onChange={v => { ct.signalModifier = v; update(); }} min={0} max={5} step={0.1} unit="×" />
                )}
                {ct.sweepRadius !== undefined && (
                  <NumInput label="Sweep radius" value={ct.sweepRadius} onChange={v => { ct.sweepRadius = v; update(); }} min={100} max={3000} step={50} unit="m" />
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// --- Styles ---

const s: Record<string, React.CSSProperties> = {
  toggleButton: {
    position: 'absolute',
    top: '50px',
    left: '10px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(12, 16, 24, 0.9)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    zIndex: 90,
  },
  panel: {
    position: 'absolute',
    top: '50px',
    left: '10px',
    width: '320px',
    maxHeight: 'calc(100vh - 160px)',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(12, 16, 24, 0.96)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
    zIndex: 90,
    fontFamily: theme.fonts.mono,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  headerTitle: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: theme.colors.accentGreen,
  },
  headerActions: {
    display: 'flex',
    gap: '6px',
  },
  resetButton: {
    padding: '2px 8px',
    background: 'none',
    border: `1px solid ${theme.colors.textDim}`,
    borderRadius: '3px',
    color: theme.colors.textDim,
    fontFamily: theme.fonts.mono,
    fontSize: '9px',
    fontWeight: 600,
    letterSpacing: '1px',
    cursor: 'pointer',
  },
  closeButton: {
    padding: '2px 6px',
    background: 'none',
    border: 'none',
    color: theme.colors.textDim,
    fontFamily: theme.fonts.mono,
    fontSize: '14px',
    cursor: 'pointer',
  },
  tabs: {
    display: 'flex',
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  tab: {
    flex: 1,
    padding: '6px 0',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: theme.colors.textDim,
    fontFamily: theme.fonts.mono,
    fontSize: '9px',
    fontWeight: 600,
    letterSpacing: '1px',
    cursor: 'pointer',
    textAlign: 'center' as const,
  },
  tabActive: {
    color: theme.colors.textPrimary,
    borderBottomColor: theme.colors.accentGreen,
  },
  content: {
    padding: '8px 12px',
    overflowY: 'auto' as const,
    flex: 1,
  },
  sectionLabel: {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    color: theme.colors.textDim,
    padding: '8px 0 4px',
    borderBottom: `1px solid ${theme.colors.border}`,
    marginBottom: '4px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '3px 0',
    gap: '8px',
  },
  label: {
    fontSize: '10px',
    color: theme.colors.textSecondary,
    whiteSpace: 'nowrap' as const,
    minWidth: '80px',
  },
  inputGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flex: 1,
    justifyContent: 'flex-end',
  },
  slider: {
    width: '80px',
    height: '4px',
    accentColor: theme.colors.accentGreen,
    cursor: 'pointer',
  },
  numInput: {
    width: '52px',
    padding: '2px 4px',
    background: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '3px',
    color: theme.colors.textPrimary,
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    textAlign: 'right' as const,
  },
  unit: {
    fontSize: '9px',
    color: theme.colors.textDim,
    minWidth: '16px',
  },
  // Card section
  cardBlock: {
    marginBottom: '1px',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    padding: '5px 4px',
    background: 'none',
    border: 'none',
    borderBottom: `1px solid ${theme.colors.border}`,
    cursor: 'pointer',
    textAlign: 'left' as const,
  },
  cardHeaderActive: {
    background: 'rgba(0, 255, 136, 0.04)',
  },
  cardCategory: {
    fontSize: '8px',
    fontWeight: 700,
    letterSpacing: '1px',
    color: theme.colors.textDim,
    fontFamily: theme.fonts.mono,
    width: '28px',
  },
  cardName: {
    fontSize: '11px',
    color: theme.colors.textPrimary,
    fontFamily: theme.fonts.mono,
    flex: 1,
  },
  cardModified: {
    color: theme.colors.accentOrange,
    fontSize: '8px',
  },
  cardChevron: {
    fontSize: '10px',
    color: theme.colors.textDim,
    fontFamily: theme.fonts.mono,
  },
  cardBody: {
    padding: '4px 8px 8px 36px',
    background: 'rgba(0, 255, 136, 0.02)',
  },
};
